import { Head, usePage } from '@inertiajs/react'
import type { SharedProps } from '../lib/types'

interface SeoProps {
  title: string
  description?: string
  image?: string | null
  type?: 'website' | 'product' | 'article'
  noindex?: boolean
  /** JSON-LD structured data object(s) for SEO/AEO. */
  jsonLd?: object | object[]
}

const SITE = 'Forma Sound'
const DEFAULT_DESC =
  'Commercial preset packs for Serum 2 and other software synthesizers. Built by sound designers, for music producers.'

// Centralized <head> management: title, meta description, canonical URL, Open
// Graph, Twitter cards, and JSON-LD structured data — all SSR-rendered so
// crawlers and AI answer engines see complete metadata.
export default function Seo({
  title,
  description = DEFAULT_DESC,
  image,
  type = 'website',
  noindex = false,
  jsonLd,
}: SeoProps) {
  const page = usePage<SharedProps>()
  const base = page.props.site?.url ?? ''
  const path = page.url.split('?')[0]
  const canonical = `${base}${path}`
  const absImage = image && base && image.startsWith('/') ? `${base}${image}` : image

  const fullTitle = title === SITE ? title : `${title} — ${SITE}`
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []

  return (
    <Head title={fullTitle}>
      <meta name="description" content={description} head-key="desc" />
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" head-key="robots" />
      ) : (
        <link rel="canonical" href={canonical} head-key="canonical" />
      )}

      <meta property="og:site_name" content={SITE} head-key="og:site" />
      <meta property="og:type" content={type} head-key="og:type" />
      <meta property="og:title" content={fullTitle} head-key="og:title" />
      <meta property="og:description" content={description} head-key="og:desc" />
      <meta property="og:url" content={canonical} head-key="og:url" />
      {absImage && <meta property="og:image" content={absImage} head-key="og:image" />}

      <meta name="twitter:card" content={absImage ? 'summary_large_image' : 'summary'} head-key="tw:card" />
      <meta name="twitter:title" content={fullTitle} head-key="tw:title" />
      <meta name="twitter:description" content={description} head-key="tw:desc" />
      {absImage && <meta name="twitter:image" content={absImage} head-key="tw:image" />}

      {blocks.map((block, i) => (
        <script
          key={i}
          type="application/ld+json"
          head-key={`jsonld-${i}`}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(block) }}
        />
      ))}
    </Head>
  )
}
